import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, AlertTriangle, X } from 'lucide-react';
import { MappingInfo, MapFileKind } from '../../../../types/clientOnboarding';
import { validateGeoJsonDocument } from '../../../../utils/geojson';

interface Props {
  data: MappingInfo;
  onChange: (data: MappingInfo) => void;
  errors: Record<string, string>;
}

// ---- R1E-07: Local-only mapping file validators ----

function validateKml(text: string): boolean {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/xml');
    // Check for parser errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) return false;
    // Require <kml> root element
    return doc.documentElement?.localName === 'kml';
  } catch {
    return false;
  }
}

const SUPPORTED_KML_EXT = '.kml';
const SUPPORTED_GEOJSON_EXTS = ['.geojson', '.json'];

// ---- R1H-02: Centralised mapping file-size policy ----

/** Maximum allowed mapping file size in bytes (10 MiB). */
export const MAX_MAPPING_FILE_BYTES = 10 * 1024 * 1024; // 10 MiB

const MappingStep: React.FC<Props> = ({ data, onChange, errors }) => {
  const { t } = useTranslation(['admin_client_onboarding']);
  const kmlRef = useRef<HTMLInputElement>(null);
  const geojsonRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // ---- R1F-02: Race condition guards ----

  // Latest-state reference — always reflects current props, never stale closure
  const latestDataRef = useRef(data);
  useEffect(() => {
    latestDataRef.current = data;
  }, [data]);

  // Request version — incremented on each file selection, clear, and unmount.
  // An async read whose version doesn't match MUST NOT apply its result.
  const fileRequestVersionRef = useRef(0);

  const resetMapping = () => {
    // Invalidate every pending file read
    ++fileRequestVersionRef.current;

    onChange({
      ...latestDataRef.current,
      file_kind: null,
      file_name: null,
      file_size_bytes: null,
    });
    setFileError(null);
    if (kmlRef.current) kmlRef.current.value = '';
    if (geojsonRef.current) geojsonRef.current.value = '';
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>, kind: MapFileKind) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Invalidate all prior pending reads
    const requestVersion = ++fileRequestVersionRef.current;

    setFileError(null);

    // Validate extension
    const fileName = file.name.toLowerCase();
    if (kind === 'kml') {
      if (!fileName.endsWith(SUPPORTED_KML_EXT)) {
        setFileError(t('validation.unsupportedMappingFile'));
        if (kmlRef.current) kmlRef.current.value = '';
        return;
      }
    } else {
      const hasValidExt = SUPPORTED_GEOJSON_EXTS.some(ext => fileName.endsWith(ext));
      if (!hasValidExt) {
        setFileError(t('validation.unsupportedMappingFile'));
        if (geojsonRef.current) geojsonRef.current.value = '';
        return;
      }
    }

    // ---- R1H-02: Check file size BEFORE reading content ----
    if (file.size > MAX_MAPPING_FILE_BYTES) {
      setFileError(t('validation.mappingFileTooLarge', { maxSizeMb: (MAX_MAPPING_FILE_BYTES / (1024 * 1024)).toFixed(0) }));
      // Clear the native file input — DO NOT call file.text()
      if (kind === 'kml' && kmlRef.current) kmlRef.current.value = '';
      if (kind === 'geojson' && geojsonRef.current) geojsonRef.current.value = '';
      return;
    }

    // Read and validate file content
    try {
      const text = await file.text();

      // ---- R1F-02 guard: bail if a newer request superseded us ----
      if (requestVersion !== fileRequestVersionRef.current) return;

      const isValid = kind === 'kml' ? validateKml(text) : validateGeoJsonDocument(text);

      // ---- R1F-02 guard: bail again after validation ----
      if (requestVersion !== fileRequestVersionRef.current) return;

      if (!isValid) {
        setFileError(kind === 'kml' ? t('validation.invalidKmlFile') : t('validation.invalidGeoJsonFile'));
        if (kind === 'kml' && kmlRef.current) kmlRef.current.value = '';
        if (kind === 'geojson' && geojsonRef.current) geojsonRef.current.value = '';
        return;
      }

      // ---- R1F-02: Use latest state, not stale captured `data` ----
      onChange({
        ...latestDataRef.current,
        file_kind: kind,
        file_name: file.name,
        file_size_bytes: file.size,
      });
    } catch {
      // ---- R1F-02 guard: bail if a newer request superseded us ----
      if (requestVersion !== fileRequestVersionRef.current) return;

      setFileError(kind === 'kml' ? t('validation.invalidKmlFile') : t('validation.invalidGeoJsonFile'));
      if (kind === 'kml' && kmlRef.current) kmlRef.current.value = '';
      if (kind === 'geojson' && geojsonRef.current) geojsonRef.current.value = '';
    }
  };

  // ---- R1F-02: Invalidate pending reads on unmount ----
  useEffect(() => {
    return () => {
      ++fileRequestVersionRef.current;
    };
  }, []);

  return (
    <div className="space-y-4" data-testid="onboarding-step-mapping">
      <div>
        <h3 className="text-heading-3 text-[var(--text-primary)] mb-1">{t('step5.heading')}</h3>
        <p className="text-sm text-[var(--text-secondary)]">{t('step5.description')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* KML — keyboard-operable via label + hidden input; KMZ disabled in R1E */}
        <label
          className="border-2 border-dashed border-[var(--border-light)] rounded-lg p-6 text-center hover:border-[var(--color-primary)] transition-colors cursor-pointer focus-within:ring-2 focus-within:ring-[var(--color-primary)]"
          data-testid="onboarding-upload-kml"
        >
          <Upload className="w-8 h-8 text-[var(--text-tertiary)] mx-auto mb-2" aria-hidden="true" />
          <p className="text-sm font-medium text-[var(--text-primary)]">{t('step5.importKML')}</p>
          <p className="text-xs text-[var(--text-secondary)]">{t('step5.importKMLDesc')}</p>
          <input
            ref={kmlRef}
            type="file"
            accept=".kml"
            className="sr-only"
            onChange={e => handleFile(e, 'kml')}
            data-testid="onboarding-file-kml"
            aria-label={t('step5.importKML')}
          />
        </label>

        {/* GeoJSON — keyboard-operable via label + hidden input */}
        <label
          className="border-2 border-dashed border-[var(--border-light)] rounded-lg p-6 text-center hover:border-[var(--color-primary)] transition-colors cursor-pointer focus-within:ring-2 focus-within:ring-[var(--color-primary)]"
          data-testid="onboarding-upload-geojson"
        >
          <Upload className="w-8 h-8 text-[var(--text-tertiary)] mx-auto mb-2" aria-hidden="true" />
          <p className="text-sm font-medium text-[var(--text-primary)]">{t('step5.importGeoJSON')}</p>
          <p className="text-xs text-[var(--text-secondary)]">{t('step5.importGeoJSONDesc')}</p>
          <input
            ref={geojsonRef}
            type="file"
            accept=".geojson,.json"
            className="sr-only"
            onChange={e => handleFile(e, 'geojson')}
            data-testid="onboarding-file-geojson"
            aria-label={t('step5.importGeoJSON')}
          />
        </label>
      </div>

      {/* R1E-07: File validation error */}
      {fileError && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" role="alert" data-testid="onboarding-mapping-error">
          {fileError}
        </div>
      )}

      {/* File info with clear action */}
      {data.file_name ? (
        <div className="flex items-center justify-between gap-3 p-3 bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 rounded-lg" data-testid="onboarding-file-info">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-[var(--color-success)]" />
            <div className="text-sm">
              <p className="font-medium text-[var(--text-primary)]">{t('step5.fileSelected')}: {data.file_name}</p>
              <p className="text-[var(--text-secondary)]">{t('step5.fileSize')}: {(data.file_size_bytes ?? 0) > 1024 ? `${((data.file_size_bytes ?? 0) / 1024).toFixed(1)} KB` : `${data.file_size_bytes} B`}</p>
            </div>
          </div>
          {/* R1E-05: Clear selected mapping file */}
          <button
            onClick={resetMapping}
            className="p-1.5 text-[var(--text-tertiary)] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            aria-label={t('step5.clearFile')}
            data-testid="onboarding-clear-file"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <p className="text-sm text-[var(--text-tertiary)]" data-testid="onboarding-no-file">{t('step5.noFile')}</p>
      )}

      {/* Manual count */}
      <div>
        <label htmlFor="mapping-manual-count" className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('step5.drawManually')}</label>
        <p className="text-xs text-[var(--text-tertiary)] mb-1">{t('step5.drawManuallyDesc')}</p>
        <input
          id="mapping-manual-count"
          type="number" min={0}
          className="w-32 p-2.5 border border-[var(--border-light)] rounded-lg bg-[var(--surface)] text-[var(--text-primary)]"
          value={data.manual_count}
          onChange={e => onChange({ ...data, manual_count: Number(e.target.value) })}
          data-testid="onboarding-manual-count" />
        {errors['mapping.manual_count'] && <p className="text-[var(--color-danger)] text-xs mt-1" role="alert">{errors['mapping.manual_count']}</p>}
      </div>

      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="onboarding-mapping-note">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">{t('step5.conceptualNoteTitle')}</p>
          <p>{t('step5.conceptualNote')}</p>
          <p className="text-xs mt-1 opacity-75">{t('step5.mapPreviewNote')}</p>
        </div>
      </div>
    </div>
  );
};

export default MappingStep;
